/* Luxury Offer Countdown. Vanilla JS, no dependencies, compatible with Shopify Theme Editor. */
(() => {
  'use strict';
  if (window.__luxuryOfferCountdownLoaded) return;
  window.__luxuryOfferCountdownLoaded = true;

  // Require an explicit offset. This avoids browser-dependent timezone interpretation.
  const ISO_WITH_TZ = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:\d{2})$/i;
  const STORAGE_PREFIX = 'shopify-luxury-offer:v1:';
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const twoDigits = (n) => String(n).padStart(2, '0');
  const normalizeDate = (value) => String(value || '').replace(/^[\s\u200B-\u200F\u2060\uFEFF]+|[\s\u200B-\u200F\u2060\uFEFF]+$/g, '');

  const parseExplicitDate = (value) => {
    const cleaned = normalizeDate(value);
    if (!cleaned || !ISO_WITH_TZ.test(cleaned)) return null;
    const result = Date.parse(cleaned);
    return Number.isFinite(result) ? result : null;
  };

  function resolveDeadline(root, now) {
    if (root.dataset.locMode === 'evergreen') {
      const minutes = clamp(Number(root.dataset.locDuration) || 1440, 5, 10080);
      const identifier = (root.dataset.locCampaign || root.id).trim();
      const key = `${STORAGE_PREFIX}${root.dataset.locShop}:${identifier}:${minutes}`;
      try {
        const previous = Number(window.localStorage.getItem(key));
        if (Number.isFinite(previous) && previous > 0) return previous;
        const deadline = now + minutes * 60_000;
        window.localStorage.setItem(key, String(deadline));
        return deadline;
      } catch (_) {
        // Never silently restart a per-visitor offer when persistence is unavailable.
        return null;
      }
    }
    const metafieldEpoch = Number(root.dataset.locEpoch);
    if (Number.isFinite(metafieldEpoch) && metafieldEpoch > 0) return metafieldEpoch * 1000;
    return parseExplicitDate(root.dataset.locEnd);
  }

  function setState(root, state) {
    const active = root.querySelector('[data-loc-active]');
    const before = root.querySelector('[data-loc-before-message]');
    const expired = root.querySelector('[data-loc-expired-message]');
    if (!active || !before || !expired) return;
    active.hidden = state !== 'active';
    before.hidden = state !== 'before';
    expired.hidden = state !== 'expired';
    root.hidden = state === 'hidden';
    const slot = root.closest('.loc-gallery-slot');
    if (slot) slot.hidden = state === 'hidden';
  }

  function updateNumbers(root, milliseconds) {
    const total = Math.max(0, Math.ceil(milliseconds / 1000));
    const showDays = root.dataset.locDays === 'true';
    const days = Math.floor(total / 86400);
    const hours = showDays ? Math.floor((total % 86400) / 3600) : Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    const units = { days, hours, minutes, seconds };
    Object.entries(units).forEach(([unit, value]) => {
      const target = root.querySelector(`[data-loc-unit="${unit}"]`);
      if (target) target.textContent = twoDigits(value);
    });
    const timer = root.querySelector('[data-loc-timer]');
    // Avoid a live announcement every second, but expose a human-readable label on focus.
    if (timer) {
      const labels = [...timer.querySelectorAll('.loc-unit')].map((el) => {
        const number = el.querySelector('.loc-unit__number')?.textContent || '00';
        const label = el.querySelector('.loc-unit__label')?.textContent || '';
        return `${number} ${label}`;
      });
      timer.setAttribute('aria-label', labels.join('، '));
    }
  }

  function connectPurchaseLink(root) {
    const cta = root.querySelector('[data-loc-cta]');
    if (!cta || root.dataset.locCtaMode !== 'scroll') return;
    if (cta.dataset.locBound === 'true') return;
    cta.dataset.locBound = 'true';
    cta.addEventListener('click', (event) => {
      // Prefer a visible, main product form over sticky cart drawers or hidden forms.
      const productForms = [...document.querySelectorAll('form[action*="/cart/add"]')];
      const form = productForms.find((el) => el.getClientRects().length > 0 && !el.closest('[aria-hidden="true"]'));
      const target = form || document.querySelector('product-form:not([hidden])');
      if (!target) return; // URL fallback stays available.
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const button = target.querySelector('button[type="submit"]');
      if (button) {
        window.setTimeout(() => {
          try { button.focus({ preventScroll: true }); } catch (_) { button.focus(); }
        }, 350);
      }
    });
  }

  function initialize(root) {
    if (typeof root.__locCleanup === 'function') root.__locCleanup();
    const now = Date.now();
    const preview = root.dataset.locPreview === 'true';
    const startValue = root.dataset.locMode === 'fixed' ? normalizeDate(root.dataset.locStart) : '';
    const start = startValue ? parseExplicitDate(startValue) : null;
    let deadline = resolveDeadline(root, now);
    const note = root.querySelector('[data-loc-editor-note]');

    let problem = '';
    if (startValue && start === null) problem = 'Invalid start date. Use YYYY-MM-DDTHH:mm:ss+03:00.';
    if (deadline === null) problem = root.dataset.locMode === 'evergreen'
      ? 'Browser storage is unavailable. Per-visitor timer is hidden to avoid reset on reload.'
      : 'Set a fixed offer end date, e.g. 2026-11-05T23:59:59+03:00, or attach custom.offer_end_at.';
    if (start !== null && deadline !== null && start >= deadline && root.dataset.locMode !== 'evergreen') {
      problem = 'Start must be earlier than the offer deadline.';
    }

    if (problem) {
      if (!preview) { setState(root, 'hidden'); return; }
      deadline = now + ((3 * 24 + 12) * 3600 + 45 * 60 + 20) * 1000;
      if (note) {
        note.textContent = `EDITOR PREVIEW ONLY: ${problem} The placeholder clock is not published.`;
        note.hidden = false;
      }
    } else if (note) {
      note.hidden = true;
    }

    connectPurchaseLink(root);

    function tick() {
      if (!root.isConnected) {
        if (typeof root.__locCleanup === 'function') root.__locCleanup();
        return;
      }
      const current = Date.now();
      if (!problem && start !== null && current < start) {
        setState(root, root.dataset.locBefore === 'message' ? 'before' : 'hidden');
        return;
      }
      const remaining = deadline - current;
      if (remaining <= 0) {
        updateNumbers(root, 0);
        setState(root, root.dataset.locExpiry === 'message' ? 'expired' : 'hidden');
        if (typeof root.__locCleanup === 'function') root.__locCleanup();
        return;
      }
      updateNumbers(root, remaining);
      setState(root, 'active');
    }

    tick();
    const interval = window.setInterval(tick, 1000);
    root.__locCleanup = () => {
      window.clearInterval(interval);
      root.__locCleanup = null;
    };
  }

  // Move only the countdown content, never the Shopify section wrapper. Keeping
  // that wrapper in its original position allows Theme Editor to find its section.
  // The gallery is found structurally through an attribute on Kalles's existing
  // product grid. No extra grid child is added (to avoid nth-child layout bugs).
  function placeInGallery(root) {
    if (root.dataset.locPlacement !== 'below_gallery') return;
    const productId = root.dataset.locProductId;
    if (!productId) return;
    const grids = document.querySelectorAll('[data-loc-gallery-main]');
    const grid = [...grids].find((node) => node.dataset.locGalleryMain === productId);
    if (!grid) return; // Safe fallback: standard section remains visible.
    const children = [...grid.children];
    // Kalles prints _product_sidebar first, _product-medias second, then info.
    // Try the stable Shopify static-block ID first and fall back to that order.
    const media = children.find((node) => node.matches?.('.hdt-product-media-wrapps') || node.querySelector?.('hdt-product-media'))
      || children.find((node) => node.id?.includes('main-product-medias'))
      || children[1];
    if (!media) return;
    let slot = media.querySelector(':scope > .loc-gallery-slot');
    if (!slot) {
      slot = document.createElement('div');
      slot.className = 'loc-gallery-slot';
      slot.dataset.locGalleryProduct = productId;
      slot.hidden = true;
      media.appendChild(slot);
    }
    if (root.parentElement === slot) return;
    if (!root.__locOriginalSection) {
      root.__locOriginalSection = root.closest('.luxury-offer-countdown-section');
    }
    slot.style.setProperty('--loc-gallery-gap-desktop', root.style.getPropertyValue('--loc-gallery-gap-desktop'));
    slot.style.setProperty('--loc-gallery-gap-mobile', root.style.getPropertyValue('--loc-gallery-gap-mobile'));
    slot.appendChild(root);
    root.__locOriginalSection?.classList.add('loc-original-empty');
  }

  function restoreFromGallery(root) {
    const original = root.__locOriginalSection;
    if (!original || !original.isConnected || !root.isConnected) return;
    original.appendChild(root);
    original.classList.remove('loc-original-empty');
  }

  function scan(container = document) {
    const roots = container.matches?.('[data-loc-root]')
      ? [container]
      : [...container.querySelectorAll?.('[data-loc-root]') || []];
    roots.forEach((root) => { placeInGallery(root); initialize(root); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan());
  else scan();

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:load', () => {
    // On main-product reload, the grid returns; re-home any existing offers.
    document.querySelectorAll('[data-loc-root][data-loc-placement="below_gallery"]').forEach(placeInGallery);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const roots = event.target.matches?.('[data-loc-root]')
      ? [event.target]
      : [...event.target.querySelectorAll?.('[data-loc-root]') || []];
    // Main-product section may rerender independently in the Theme Editor.
    // Restore teleported offers before its old gallery DOM is removed.
    if (event.target.matches?.('[data-loc-gallery-main]') || event.target.querySelector?.('[data-loc-gallery-main]')) {
      roots.forEach(restoreFromGallery);
    }
    // If a countdown section itself was deleted/replaced while its content is
    // in the gallery, clean up that detached content to avoid orphan timers.
    document.querySelectorAll('[data-loc-root]').forEach((root) => {
      if (root.__locOriginalSection === event.target ||
          (root.__locOriginalSection && event.target.contains(root.__locOriginalSection))) {
        root.__locCleanup?.();
        root.remove();
      }
    });
    roots.forEach((root) => root.__locCleanup?.());
  });
})();
